import { AlertCircleIcon } from 'lucide-react';
import { Spinner } from '@@/ui/components';
import type { QueryByPathResponse } from '../api/api-entities';
import clsx from 'clsx';

function DataRenderer(props: { title: string; subtitle?: string; content: string; className?: string }) {
  return (
    <div className={clsx('flex flex-col bg-neutral-100 rounded-md!', props.className)}>
      <pre className='text-sm! text-neutral-950 overflow-auto px-3 py-2 text-wrap'>{props.content}</pre>
    </div>
  );
}

export function PathPreview(props: {
  path: string;
  data: QueryByPathResponse | null;
  error: Error | null;
  isPending: boolean;
  onPathClick: (path: string) => void;
}) {
  const { data, error, isPending } = props;

  return (
    <div className='flex flex-col overflow-hidden'>
      {isPending && (
        <div className='flex items-center gap-2 pb-3'>
          <Spinner className='size-4' />
          <p className='text-sm text-neutral-950'>Loading</p>
        </div>
      )}

      {error && (
        <div className='flex items-center gap-2 pb-3'>
          <AlertCircleIcon className='size-4 text-red-950' />
          <p className='text-sm font-medium text-red-950'>Path cannot be evaluated.</p>
        </div>
      )}

      {!isPending && !error && data && (
        <div className='flex flex-col'>
          {Object.keys(data.evaluated).length > 0 && (
            <div className='flex-1 pb-3'>
              <h4 className='text-xs font-medium text-slate-950 mb-2'>Evaluated</h4>
              {data.evaluated && (
                <DataRenderer
                  title='Evaluated'
                  className='rounded-none'
                  content={JSON.stringify(data.evaluated, null, 2)}
                />
              )}
            </div>
          )}
          <div className='flex-1 pb-3'>
            <h4 className='text-xs font-medium text-slate-950 mb-2'>
              Entities
              {data.entities && data.entities.length > 0 && (
                <span className='text-xs font-medium rounded-full text-slate-950 ml-1 border px-2 py-0.5'>
                  {data.entities.length}
                </span>
              )}
            </h4>
            {data.entities && data.entities.length > 0 ? (
              <div className='border border-slate-300 divide-y divide-slate-300 rounded-md overflow-hidden'>
                {data.entities.map((entity, index) => (
                  <div
                    key={entity.entityId || index}
                    className={'flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-100'}
                    onClick={() => props.onPathClick(entity.path)}
                  >
                    <div className='flex justify-between items-center w-full'>
                      <div className='font-semibold text-sm text-slate-950 truncate max-w-sm'>
                        {entity.name}
                        {entity.description && (
                          <div className='text-xs text-slate-600 font-normal'>{entity.description}</div>
                        )}
                      </div>
                      <div className='text-xs text-slate-600'>{entity.entityId}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-xs text-slate-600'>No entities found.</p>
            )}
          </div>

          <div className='flex-1 py-4'>
            <h4 className='text-xs font-medium text-slate-950 mb-2'>
              Items
              {data.items && data.items.length > 0 && (
                <span className='text-xs font-medium rounded-full text-slate-950 ml-1 border px-2 py-0.5'>
                  {data.items.length}
                </span>
              )}
            </h4>
            {data.items && data.items.length > 0 ? (
              <div className='border border-slate-300 divide-y divide-slate-300 rounded-md overflow-hidden'>
                {data.items.map((item, index) => (
                  <div
                    key={item.itemId || index}
                    className={'flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-100'}
                    onClick={() => props.onPathClick(`${props.path}${item.primaryKey}`)}
                  >
                    <div className='flex justify-between items-center w-full'>
                      <div className='font-semibold text-sm text-slate-950 truncate max-w-sm'>{item.primaryKey}</div>
                      <div className='text-xs text-slate-600'>{item.itemId}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-xs text-slate-600'>No items found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
